/**
 * Auth store — backend ADR-0008 compliant.
 *
 * Access token: held in JavaScript memory ONLY. Never written to localStorage
 * or sessionStorage. A hard reload clears it; the app calls the refresh endpoint
 * on mount to rehydrate (the browser sends the httpOnly cookie automatically).
 *
 * Refresh token: per backend ADR-0008 it should live in an httpOnly cookie set by the
 * backend. JS cannot read it; it is sent automatically on credentialed requests.
 *
 * CONTRACT (verified against backend/api/openapi/zenith.openapi.json):
 *  - POST /login    -> { token: IdentityAuthToken }, Set-Cookie: refresh (snake_case wire)
 *  - POST /refresh  -> { token: IdentityAuthToken }, rotates the refresh cookie
 *  - POST /logout   -> {} , clears the refresh cookie
 *  - GET  /userinfo -> IdentityUser
 */
import { defineStore } from "pinia";
import { axiosInstance } from "@/lib/client";
import { queryClient } from "@/lib/query";
import { useTeamStore } from "@/stores/team";
import { useLocaleStore } from "@/stores/locale";
import type { IdentityUser } from "@/api/model/identityUser";
import type { IdentityLoginResponse } from "@/api/model/identityLoginResponse";
import type { IdentityRefreshResponse } from "@/api/model/identityRefreshResponse";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    /** Access token — in memory only. Null means unauthenticated / not yet hydrated. */
    accessToken: null as string | null,
    /** Authenticated user profile (wire shape from /userinfo). */
    user: null as IdentityUser | null,
    /** True once the initial refresh hydration attempt has completed. */
    hydrated: false,
  }),

  getters: {
    isLoggedIn: (state): boolean => !!state.accessToken,
    isPlatformAdmin: (state): boolean => !!state.user?.is_platform_admin,
    displayName: (state): string => {
      if (!state.user) return "";
      const name = [state.user.first_name, state.user.last_name].filter(Boolean).join(" ");
      return name || state.user.email || "";
    },
  },

  actions: {
    setAccessToken(token: string) {
      this.accessToken = token;
    },

    clearAuth() {
      this.accessToken = null;
      this.user = null;
      // Session ended — drop the current-team context and every cached query,
      // so the next user in this tab never sees the previous user's data
      // (e.g. an empty team list redirecting a manager to /no-team).
      useTeamStore().clear();
      queryClient.clear();
    },

    /** Fetch the authenticated user's profile. Requires a valid access token. */
    async fetchUser() {
      const res = await axiosInstance.get<IdentityUser>("/userinfo");
      this.user = res.data;
      // The stored account locale wins over the browser default and any stale
      // localStorage choice left by another person on this device.
      useLocaleStore().applyAccountLocale(this.user.locale);
    },

    /**
     * Called once at app startup (router guard). Exchanges the httpOnly refresh
     * cookie for a fresh access token, then loads the profile. If the cookie is
     * absent/expired the request 401s and the user is simply not logged in —
     * that is expected, not an error.
     */
    async hydrate() {
      if (this.hydrated) return;

      try {
        const res = await axiosInstance.post<IdentityRefreshResponse>("/refresh");
        const token = res.data.token?.access_token;
        if (!token) throw new Error("no access token in refresh response");
        this.accessToken = token;
        await this.fetchUser();
      } catch {
        this.clearAuth();
      } finally {
        this.hydrated = true;
      }
    },

    /**
     * Log in with email + password. POST /login returns the access token in the
     * body and sets the httpOnly refresh cookie (backend ADR-0008).
     */
    async login(email: string, password: string) {
      const res = await axiosInstance.post<IdentityLoginResponse>("/login", {
        email,
        password,
      });
      const token = res.data.token?.access_token;
      if (!token) throw new Error("login response missing access token");
      this.accessToken = token;
      await this.fetchUser();
    },

    /**
     * Log out. Calls the backend to clear the httpOnly refresh cookie, then wipes
     * in-memory state regardless of the call's outcome.
     */
    async logout() {
      try {
        await axiosInstance.post("/logout");
      } catch {
        // Best effort: clear client state even if the network call fails.
      } finally {
        this.clearAuth();
      }
    },
  },
});
